/**
 * Draw controls component
 * Controls for drawing/selecting areas on the map
 * Supports both rectangle and freehand polygon drawing
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { useMap } from 'react-leaflet';
import { Button, Tooltip } from 'antd';
import {
  BorderOutlined,
  DragOutlined,
  DeleteOutlined,
  AimOutlined,
  FullscreenOutlined,
  EditOutlined,
} from '@ant-design/icons';
import type { Rectangle as LeafletRectangle, Polygon as LeafletPolygon } from 'leaflet';
import L from 'leaflet';
import type { Bounds, PolygonCoordinates, SelectionType } from '@/types/grid';

/**
 * Drawing mode types
 */
export type DrawMode = 'view' | 'rectangle' | 'freehand';

interface DrawControlsProps {
  /** Callback when bounds change (for rectangle or polygon bounding box) */
  onBoundsChange?: (bounds: Bounds | undefined) => void;
  /** Callback when polygon coordinates change (for freehand mode) */
  onPolygonChange?: (polygon: PolygonCoordinates | undefined) => void;
  /** Callback when selection type changes */
  onSelectionTypeChange?: (type: SelectionType) => void;
  /** Initial bounds to display */
  initialBounds?: Bounds;
  /** Initial polygon to display */
  initialPolygon?: PolygonCoordinates;
  /** Current draw mode */
  mode?: DrawMode;
  /** Callback when mode changes */
  onModeChange?: (mode: DrawMode) => void;
}

/**
 * Calculate bounding box from polygon coordinates
 */
function calculateBoundsFromPolygon(polygon: PolygonCoordinates): Bounds {
  let north = -Infinity;
  let south = Infinity;
  let east = -Infinity;
  let west = Infinity;

  for (const point of polygon) {
    if (point.lat > north) north = point.lat;
    if (point.lat < south) south = point.lat;
    if (point.lng > east) east = point.lng;
    if (point.lng < west) west = point.lng;
  }

  return { north, south, east, west };
}

/**
 * Simplify polygon using Douglas-Peucker algorithm
 * Reduces the number of points while preserving the shape
 */
function simplifyPolygon(points: PolygonCoordinates, tolerance: number = 0.0001): PolygonCoordinates {
  if (points.length <= 3) return points;

  // Find the point with maximum distance from the line between first and last
  let maxDistance = 0;
  let maxIndex = 0;

  const first = points[0];
  const last = points[points.length - 1];

  for (let i = 1; i < points.length - 1; i++) {
    const distance = perpendicularDistance(points[i], first, last);
    if (distance > maxDistance) {
      maxDistance = distance;
      maxIndex = i;
    }
  }

  // If max distance is greater than tolerance, recursively simplify
  if (maxDistance > tolerance) {
    const left = simplifyPolygon(points.slice(0, maxIndex + 1), tolerance);
    const right = simplifyPolygon(points.slice(maxIndex), tolerance);
    return [...left.slice(0, -1), ...right];
  }

  // Otherwise, return just the endpoints
  return [first, last];
}

/**
 * Calculate perpendicular distance from point to line
 */
function perpendicularDistance(
  point: { lat: number; lng: number },
  lineStart: { lat: number; lng: number },
  lineEnd: { lat: number; lng: number }
): number {
  const dx = lineEnd.lng - lineStart.lng;
  const dy = lineEnd.lat - lineStart.lat;

  if (dx === 0 && dy === 0) {
    return Math.sqrt(
      Math.pow(point.lng - lineStart.lng, 2) + Math.pow(point.lat - lineStart.lat, 2)
    );
  }

  const t = ((point.lng - lineStart.lng) * dx + (point.lat - lineStart.lat) * dy) / (dx * dx + dy * dy);
  const closestLng = lineStart.lng + t * dx;
  const closestLat = lineStart.lat + t * dy;

  return Math.sqrt(Math.pow(point.lng - closestLng, 2) + Math.pow(point.lat - closestLat, 2));
}

/**
 * Map controls for drawing rectangles and freehand polygons
 */
function DrawControlsInner({
  onBoundsChange,
  onPolygonChange,
  onSelectionTypeChange,
  initialBounds,
  initialPolygon,
  mode = 'view',
  onModeChange,
}: DrawControlsProps) {
  const map = useMap();
  const rectangleRef = useRef<LeafletRectangle | null>(null);
  const polygonRef = useRef<LeafletPolygon | null>(null);
  const polylineRef = useRef<L.Polyline | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPoint, setStartPoint] = useState<L.LatLng | null>(null);
  const [polygonPoints, setPolygonPoints] = useState<L.LatLng[]>([]);
  const [hasSelection, setHasSelection] = useState(false);

  // Track whether we have a selection
  useEffect(() => {
    setHasSelection(!!(rectangleRef.current || polygonRef.current));
  }, [rectangleRef.current, polygonRef.current]);

  // Clear all drawings
  const clearAllDrawings = useCallback(() => {
    if (rectangleRef.current) {
      map.removeLayer(rectangleRef.current);
      rectangleRef.current = null;
    }
    if (polygonRef.current) {
      map.removeLayer(polygonRef.current);
      polygonRef.current = null;
    }
    if (polylineRef.current) {
      map.removeLayer(polylineRef.current);
      polylineRef.current = null;
    }
    setHasSelection(false);
  }, [map]);

  // Initialize with initial bounds or polygon
  useEffect(() => {
    // If we have an initial polygon, draw it
    if (initialPolygon && initialPolygon.length >= 3 && !polygonRef.current && !rectangleRef.current) {
      const latLngs = initialPolygon.map(p => L.latLng(p.lat, p.lng));
      polygonRef.current = L.polygon(latLngs, {
        color: '#722ed1',
        weight: 2,
        fillOpacity: 0.15,
      }).addTo(map);
      setHasSelection(true);
    }
    // If we have initial bounds (and no polygon), draw rectangle
    else if (initialBounds && !rectangleRef.current && !polygonRef.current) {
      const bounds: L.LatLngBoundsExpression = [
        [initialBounds.south, initialBounds.west],
        [initialBounds.north, initialBounds.east],
      ];
      rectangleRef.current = L.rectangle(bounds, {
        color: '#1677ff',
        weight: 2,
        fillOpacity: 0.1,
      }).addTo(map);
      setHasSelection(true);
    }

    return () => {
      clearAllDrawings();
    };
  }, [map, initialBounds, initialPolygon, clearAllDrawings]);

  // Handle drawing mode - disable/enable map interactions
  useEffect(() => {
    if (mode === 'rectangle' || mode === 'freehand') {
      // Disable all map interactions that interfere with drawing
      map.dragging.disable();
      map.doubleClickZoom.disable();
      map.scrollWheelZoom.disable();
      map.boxZoom.disable();
      map.keyboard.disable();
      if (map.tap) map.tap.disable();

      // Change cursor to indicate drawing mode
      map.getContainer().style.cursor = 'crosshair';
    } else {
      // Re-enable all map interactions
      map.dragging.enable();
      map.doubleClickZoom.enable();
      map.scrollWheelZoom.enable();
      map.boxZoom.enable();
      map.keyboard.enable();
      if (map.tap) map.tap.enable();

      // Reset cursor
      map.getContainer().style.cursor = '';

      // Reset drawing state
      setIsDrawing(false);
      setStartPoint(null);
      setPolygonPoints([]);

      // Clean up any in-progress polyline
      if (polylineRef.current) {
        map.removeLayer(polylineRef.current);
        polylineRef.current = null;
      }
    }

    return () => {
      // Ensure interactions are re-enabled on cleanup
      map.dragging.enable();
      map.doubleClickZoom.enable();
      map.scrollWheelZoom.enable();
      map.boxZoom.enable();
      map.keyboard.enable();
      if (map.tap) map.tap.enable();
      map.getContainer().style.cursor = '';
    };
  }, [map, mode]);

  // Handle RECTANGLE drawing events
  useEffect(() => {
    if (mode !== 'rectangle') return;

    const handleMouseDown = (e: L.LeafletMouseEvent) => {
      L.DomEvent.preventDefault(e.originalEvent);
      L.DomEvent.stopPropagation(e.originalEvent);

      setIsDrawing(true);
      setStartPoint(e.latlng);

      // Clear existing drawings when starting new
      clearAllDrawings();
    };

    const handleMouseMove = (e: L.LeafletMouseEvent) => {
      if (!startPoint) return;

      const bounds = L.latLngBounds(startPoint, e.latlng);

      if (rectangleRef.current) {
        rectangleRef.current.setBounds(bounds);
      } else {
        rectangleRef.current = L.rectangle(bounds, {
          color: '#1677ff',
          weight: 2,
          fillOpacity: 0.1,
          dashArray: '5, 5',
        }).addTo(map);
      }
    };

    const handleMouseUp = (e: L.LeafletMouseEvent) => {
      if (!startPoint) return;

      const bounds = L.latLngBounds(startPoint, e.latlng);

      // Only accept if the rectangle has some minimum size
      const minSize = 0.0001;
      const latDiff = Math.abs(bounds.getNorth() - bounds.getSouth());
      const lngDiff = Math.abs(bounds.getEast() - bounds.getWest());

      if (latDiff < minSize || lngDiff < minSize) {
        clearAllDrawings();
        setIsDrawing(false);
        setStartPoint(null);
        return;
      }

      // Update rectangle style to solid
      if (rectangleRef.current) {
        rectangleRef.current.setStyle({ dashArray: undefined });
      }

      // Emit bounds and selection type
      onBoundsChange?.({
        north: bounds.getNorth(),
        south: bounds.getSouth(),
        east: bounds.getEast(),
        west: bounds.getWest(),
      });
      onPolygonChange?.(undefined);
      onSelectionTypeChange?.('rectangle');

      setIsDrawing(false);
      setStartPoint(null);
      setHasSelection(true);

      // Switch back to view mode
      onModeChange?.('view');
    };

    map.on('mousedown', handleMouseDown);
    map.on('mousemove', handleMouseMove);
    map.on('mouseup', handleMouseUp);

    return () => {
      map.off('mousedown', handleMouseDown);
      map.off('mousemove', handleMouseMove);
      map.off('mouseup', handleMouseUp);
    };
  }, [map, mode, startPoint, onBoundsChange, onPolygonChange, onSelectionTypeChange, onModeChange, clearAllDrawings]);

  // Handle FREEHAND drawing events
  useEffect(() => {
    if (mode !== 'freehand') return;

    const handleMouseDown = (e: L.LeafletMouseEvent) => {
      L.DomEvent.preventDefault(e.originalEvent);
      L.DomEvent.stopPropagation(e.originalEvent);

      setIsDrawing(true);
      setPolygonPoints([e.latlng]);

      // Clear existing drawings when starting new
      clearAllDrawings();

      // Start the polyline
      polylineRef.current = L.polyline([e.latlng], {
        color: '#722ed1',
        weight: 3,
        dashArray: '5, 5',
      }).addTo(map);
    };

    const handleMouseMove = (e: L.LeafletMouseEvent) => {
      if (!isDrawing || !polylineRef.current) return;

      // Add point to polyline
      polylineRef.current.addLatLng(e.latlng);
      setPolygonPoints(prev => [...prev, e.latlng]);
    };

    const handleMouseUp = () => {
      if (!isDrawing) return;

      // Get the collected points
      const points = polygonPoints;

      // Need at least 3 points for a polygon
      if (points.length < 3) {
        clearAllDrawings();
        setIsDrawing(false);
        setPolygonPoints([]);
        return;
      }

      // Remove the temporary polyline
      if (polylineRef.current) {
        map.removeLayer(polylineRef.current);
        polylineRef.current = null;
      }

      // Convert to our coordinate format and simplify
      const rawCoords: PolygonCoordinates = points.map(p => ({ lat: p.lat, lng: p.lng }));
      const simplifiedCoords = simplifyPolygon(rawCoords, 0.0002);

      // Ensure we still have at least 3 points after simplification
      if (simplifiedCoords.length < 3) {
        clearAllDrawings();
        setIsDrawing(false);
        setPolygonPoints([]);
        return;
      }

      // Close the polygon (first point = last point)
      if (simplifiedCoords[0].lat !== simplifiedCoords[simplifiedCoords.length - 1].lat ||
          simplifiedCoords[0].lng !== simplifiedCoords[simplifiedCoords.length - 1].lng) {
        simplifiedCoords.push({ ...simplifiedCoords[0] });
      }

      // Create the polygon on the map
      const latLngs = simplifiedCoords.map(p => L.latLng(p.lat, p.lng));
      polygonRef.current = L.polygon(latLngs, {
        color: '#722ed1',
        weight: 2,
        fillOpacity: 0.15,
      }).addTo(map);

      // Calculate bounding box
      const bounds = calculateBoundsFromPolygon(simplifiedCoords);

      // Emit both bounds and polygon
      onBoundsChange?.(bounds);
      onPolygonChange?.(simplifiedCoords);
      onSelectionTypeChange?.('polygon');

      setIsDrawing(false);
      setPolygonPoints([]);
      setHasSelection(true);

      // Switch back to view mode
      onModeChange?.('view');
    };

    map.on('mousedown', handleMouseDown);
    map.on('mousemove', handleMouseMove);
    map.on('mouseup', handleMouseUp);

    return () => {
      map.off('mousedown', handleMouseDown);
      map.off('mousemove', handleMouseMove);
      map.off('mouseup', handleMouseUp);
    };
  }, [map, mode, isDrawing, polygonPoints, onBoundsChange, onPolygonChange, onSelectionTypeChange, onModeChange, clearAllDrawings]);

  const handleClear = () => {
    clearAllDrawings();
    onBoundsChange?.(undefined);
    onPolygonChange?.(undefined);
  };

  const handleFitBounds = () => {
    if (rectangleRef.current) {
      map.fitBounds(rectangleRef.current.getBounds(), { padding: [50, 50] });
    } else if (polygonRef.current) {
      map.fitBounds(polygonRef.current.getBounds(), { padding: [50, 50] });
    }
  };

  const handleLocateUser = () => {
    map.locate({ setView: true, maxZoom: 14 });
  };

  return (
    <div
      style={{
        position: 'absolute',
        top: 10,
        left: 10,
        zIndex: 1000,
        background: 'var(--color-bg-elevated, rgba(255,255,255,0.95))',
        backdropFilter: 'blur(8px)',
        borderRadius: 6,
        padding: 4,
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        display: 'flex',
        alignItems: 'center',
        gap: 4,
      }}
    >
      {/* Drawing mode buttons */}
      <Tooltip title="Pan mode">
        <Button
          type={mode === 'view' ? 'primary' : 'text'}
          size="small"
          icon={<DragOutlined />}
          onClick={() => onModeChange?.('view')}
          style={{ minWidth: 32 }}
        />
      </Tooltip>

      <Tooltip title="Draw rectangle">
        <Button
          type={mode === 'rectangle' ? 'primary' : 'text'}
          size="small"
          icon={<BorderOutlined />}
          onClick={() => onModeChange?.('rectangle')}
          style={{ minWidth: 32 }}
        />
      </Tooltip>

      <Tooltip title="Freehand draw">
        <Button
          type={mode === 'freehand' ? 'primary' : 'text'}
          size="small"
          icon={<EditOutlined />}
          onClick={() => onModeChange?.('freehand')}
          style={{ minWidth: 32 }}
        />
      </Tooltip>

      {/* Divider */}
      <div style={{ width: 1, height: 20, background: 'var(--color-border, #e0e0e0)', margin: '0 2px' }} />

      {/* Action buttons */}
      <Tooltip title="Fit to selection">
        <Button
          type="text"
          size="small"
          icon={<FullscreenOutlined />}
          onClick={handleFitBounds}
          disabled={!hasSelection}
          style={{ minWidth: 32 }}
        />
      </Tooltip>

      <Tooltip title="My location">
        <Button
          type="text"
          size="small"
          icon={<AimOutlined />}
          onClick={handleLocateUser}
          style={{ minWidth: 32 }}
        />
      </Tooltip>

      <Tooltip title="Clear selection">
        <Button
          type="text"
          size="small"
          icon={<DeleteOutlined />}
          onClick={handleClear}
          disabled={!hasSelection}
          danger
          style={{ minWidth: 32 }}
        />
      </Tooltip>
    </div>
  );
}

/**
 * Wrapper to use DrawControls outside MapContainer
 */
export function DrawControls(props: DrawControlsProps) {
  return <DrawControlsInner {...props} />;
}

export default DrawControls;
